/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState } from "react";
import type { ReactNode } from "react";
import type {
  DigitalSignatureData,
  SignatureType,
  FileToSign,
  Signer,
  SignersSelectedType,
  SignerType,
} from "../types";

interface DigitalSignatureContextType {
  // Form data
  title: string;
  setTitle: (title: string) => void;
  description: string;
  setDescription: (description: string) => void;

  // Signature type
  selectedSignatureType: SignatureType | null;
  setSelectedSignatureType: (type: SignatureType | null) => void;

  // Files to sign
  selectedFiles: FileToSign[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<FileToSign[]>>;

  // Signers
  selectedSigners: SignersSelectedType;
  setSelectedSigners: React.Dispatch<React.SetStateAction<SignersSelectedType>>;
  addSigner: (
    signer: Signer | undefined,
    type: SignerType,
    index: number,
  ) => void;
  removeSigner: (id?: string) => void;
  updateSigner: (index: number, signer: Signer, type: SignerType) => void;

  // Validation
  isFormValid: boolean;

  // Actions
  resetForm: () => void;
  getFormData: () => DigitalSignatureData | null;
}

const DigitalSignatureContext = createContext<
  DigitalSignatureContextType | undefined
>(undefined);

export const useDigitalSignatureContext = () => {
  const context = useContext(DigitalSignatureContext);
  if (context === undefined) {
    throw new Error(
      "useDigitalSignatureContext must be used within a DigitalSignatureProvider",
    );
  }
  return context;
};

interface DigitalSignatureProviderProps {
  children: ReactNode;
}

export const DigitalSignatureProvider: React.FC<
  DigitalSignatureProviderProps
> = ({ children }) => {
  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [selectedSignatureType, setSelectedSignatureType] =
    useState<SignatureType | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<FileToSign[]>([]);
  const [selectedSigners, setSelectedSigners] = useState<SignersSelectedType>(
    [],
  );

  // Signer management functions
  const addSigner = (
    signer: Signer | undefined,
    type: SignerType,
    index: number,
  ) => {
    const newGroup: { primary: Signer | null; backup: Signer | null } = {
      primary: null,
      backup: null,
    };

    if (signer) {
      const existInANotherGroup = selectedSigners.some(
        (s) => s?.primary?.id === signer.id || s.backup?.id === signer.id,
      );

      if (existInANotherGroup) return;

      if (!selectedSigners[index]) {
        newGroup[type] = signer;
        setSelectedSigners((prev) => [...prev, newGroup]);
      }
    } else {
      setSelectedSigners((prev) => [...prev, newGroup]);
    }
  };

  const removeSigner = (id?: string) => {
    setSelectedSigners((prev) => {
      const updated = prev
        .map((pair) => {
          const primaryMatch = pair.primary?.id === id;
          const backupMatch = pair.backup?.id === id;

          if (!primaryMatch && !backupMatch) return pair;

          if (primaryMatch && pair.backup) {
            return { primary: null, backup: pair.backup };
          }

          if (backupMatch && pair.primary) {
            return { primary: pair.primary, backup: null };
          }

          return null;
        })
        .filter(Boolean) as typeof prev;

      if (updated.length === 0) {
        return [{ primary: null, backup: null }];
      }

      return updated;
    });
  };

  const updateSigner = (index: number, signer: Signer, type: SignerType) => {
    setSelectedSigners((prev) => {
      const updated = [...prev];
      updated[index][type] = signer;
      return updated.filter((s) => s !== undefined); // Limpiar undefined
    });
  };

  // Validation logic
  const hasPrimary = selectedSigners.filter((pair) => !pair.primary);

  const isFormValid =
    title.trim() !== "" &&
    selectedSignatureType !== null &&
    selectedFiles.length > 0 &&
    hasPrimary.length === 0 &&
    selectedSigners.length > 0;

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setSelectedSignatureType(null);
    setSelectedFiles([]);
    setSelectedSigners([]);
  };

  const getFormData = (): DigitalSignatureData | null => {
    if (!isFormValid || !selectedSignatureType) return null;

    return {
      title: title.trim(),
      description: description.trim() || undefined,
      signatureTypeId: selectedSignatureType.id,
      signatureTypeName: selectedSignatureType.name,
      filesToSign: selectedFiles,
      signers: selectedSigners,
    };
  };

  const value: DigitalSignatureContextType = {
    title,
    setTitle,
    description,
    setDescription,
    selectedSignatureType,
    setSelectedSignatureType,
    selectedFiles,
    setSelectedFiles,
    selectedSigners,
    setSelectedSigners,
    addSigner,
    removeSigner,
    updateSigner,
    isFormValid,
    resetForm,
    getFormData,
  };

  return (
    <DigitalSignatureContext.Provider value={value}>
      {children}
    </DigitalSignatureContext.Provider>
  );
};
